/**
 * Generic section component for displaying Weave objects (models or datasets).
 * Renders a grid of object cards with loading and empty states.
 */

import {Button} from '@wandb/components';
import {IconName} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'PotentialRoadmap' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {useWFHooks} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useHistory} from 'react-router-dom';

import {EMPTY_STATE_MIN_HEIGHT, OBJECT_CARD_LIMIT} from '../constants';
import {EmptyState} from './EmptyState';
import {ObjectCard} from './ObjectCard';

interface ObjectsSectionProps {
  entityName: string;
  projectName: string;
  objectClass: 'Model' | 'Dataset';
  title: string;
  viewAllPath: string;
  emptyMessage: string;
  emptySubtitle: string;
  emptyIcon: IconName;
  emptyIconColor: string;
  emptyIconBgGradient: string;
}

export const ObjectsSection: React.FC<ObjectsSectionProps> = ({
  entityName,
  projectName,
  objectClass,
  title,
  viewAllPath,
  emptyMessage,
  emptySubtitle,
  emptyIcon,
  emptyIconColor,
  emptyIconBgGradient,
}) => {
  const {useRootObjectVersions} = useWFHooks();
  const history = useHistory();
  const objects = useRootObjectVersions({
    entity: entityName,
    project: projectName,
    filter: {baseObjectClasses: [objectClass], latestOnly: true},
    metadataOnly: true,
  });

  const hasObjects = objects.result && objects.result.length > 0;

  return (
    <Tailwind>
      <div className="mb-32">
        <div className="mb-16 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-moon-500">
            {title}
          </h3>
          {hasObjects && (
            <Button
              endIcon="forward-next"
              variant="ghost"
              onClick={() => {
                history.push(
                  `/${entityName}/${projectName}/weave/${viewAllPath}`
                );
              }}
              data-test="launch-drawer-back"
              size="sm">
              View all
            </Button>
          )}
        </div>

        {objects.loading ? (
          <div
            className="flex items-center justify-center rounded-xl border border-moon-250 bg-white shadow-sm"
            style={{minHeight: `${EMPTY_STATE_MIN_HEIGHT}px`}}>
            <LoadingDots />
          </div>
        ) : hasObjects ? (
          <div className="grid grid-cols-3 gap-16">
            {objects.result?.slice(0, OBJECT_CARD_LIMIT).map(obj => (
              <ObjectCard
                key={obj.versionHash}
                name={obj.objectId}
                versionIndex={obj.versionIndex}
                digest={obj.versionHash}
                createdAt={
                  obj.createdAtMs
                    ? new Date(obj.createdAtMs).toISOString()
                    : undefined
                }
                type={objectClass.toLowerCase() as 'model' | 'dataset'}
                entityName={entityName}
                projectName={projectName}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={emptyIcon}
            message={emptyMessage}
            subtitle={emptySubtitle}
            iconColor={emptyIconColor}
            iconBgGradient={emptyIconBgGradient}
          />
        )}
      </div>
    </Tailwind>
  );
};
